import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalShell } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import { useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";
import type { PackagingProduct } from "@/lib/b2bPortalData";

export const Route = createFileRoute("/business/portal/catalog")({
  component: PackagingCatalogPage,
});

function PackagingCatalogPage() {
  const portal = useBusinessPortalSnapshot();
  const { account, catalog } = portal.data;

  return (
    <PortalShell
      active="/business/portal/catalog"
      eyebrow="Catalog"
      title="My catalog"
      description="Packaging approved for your account."
      account={account}
      actions={(
        <>
          <button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>
            Refresh
          </button>
          <Link to="/business/portal/projects/new" className="b2b-primary-button">
            Start order
          </Link>
        </>
      )}
    >
      <SEOHead title="Customer Packaging Catalog" description="Order branded packaging from your Quuil catalog." path="/business/portal/catalog" robots="noindex,follow" />

      {portal.loading ? (
        <section className="b2b-card-grid b2b-catalog-grid" aria-label="Loading catalog">
          {["A", "B", "C"].map((item) => (
            <article key={item} className="b2b-card b2b-loading-card">
              <div />
              <div />
              <div />
            </article>
          ))}
        </section>
      ) : catalog.length > 0 ? (
        <section className="b2b-card-grid b2b-catalog-grid">
          {catalog.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </section>
      ) : (
        <section className="b2b-card b2b-empty-state">
          <h2>No catalog items yet</h2>
          <p>Your approved packaging options will appear here after your workspace catalog is connected.</p>
          <button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>
            Refresh catalog
          </button>
        </section>
      )}
    </PortalShell>
  );
}

function ProductCard({ product }: { product: PackagingProduct }) {
  const minMoq = product.variants.length ? Math.min(...product.variants.map((variant) => variant.moq)) : null;
  const minLeadTime = product.variants[0]?.leadTime ?? "Quote required";

  return (
    <article className="b2b-card b2b-packaging-card">
      <div className={`b2b-packaging-thumb bg-gradient-to-br ${product.mockupStyle}`}>
        <span>{product.category}</span>
      </div>
      <span className="b2b-kicker">{product.productFamily}</span>
      <h2>{product.title}</h2>
      <p>{product.description}</p>
      <dl>
        <div><dt>Minimum</dt><dd>{minMoq ? `${minMoq.toLocaleString()} units` : "Quote required"}</dd></div>
        <div><dt>Timing</dt><dd>{minLeadTime}</dd></div>
        <div><dt>Regions</dt><dd>{product.regions.join(", ") || "Confirm at quote"}</dd></div>
      </dl>
      <div className="b2b-package-footer">
        <div className="b2b-package-tags" aria-label={`${product.title} print areas`}>
          <span className="b2b-tag-label">Print areas</span>
          <div className="b2b-chip-row">
            {product.printZones.map((zone) => <span key={zone.id}>{zone.name}</span>)}
          </div>
        </div>
        <Link to="/business/portal/projects/new" className="b2b-card-link">Start order</Link>
      </div>
    </article>
  );
}
